// externals
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

// types
import type { FuelEconomyDisplaySection } from '../store/selectors.types';

// store
import { vehicleDetailsSelectors } from '../store';

// utils
import { toDisplayMetric, toFuelEconomyMetric } from '../utils/fuelEconomyUtils';

/**
 * Hook for fuel economy section.
 * Resolves each metric from its standard field, falling back to the MPGe equivalent field,
 * derives the label each metric renders with, and whether the section has enough data to render.
 */
export const useFuelEconomy = (): FuelEconomyDisplaySection => {
    const fuelEconomy = useSelector(vehicleDetailsSelectors.getFuelEconomy);

    return useMemo(() => {
        const city = toFuelEconomyMetric(fuelEconomy.cityFuelEconomy, fuelEconomy.cityFuelEconomyEquivalent);
        const highway = toFuelEconomyMetric(fuelEconomy.highwayFuelEconomy, fuelEconomy.highwayFuelEconomyEquivalent);
        const combined = toFuelEconomyMetric(fuelEconomy.combinedFuelEconomy, fuelEconomy.combinedFuelEconomyEquivalent);

        return {
            city: toDisplayMetric('City', city),
            highway: toDisplayMetric('Highway', highway),
            combined: toDisplayMetric('Combined', combined),
            hasFuelEconomy: [city, highway, combined].every((metric) => Boolean(metric.value))
        };
    }, [fuelEconomy]);
};
